<template>
  <v-dialog
    v-model="dialog"
    :max-width="modalLayout.SM"
    v-bind="modalP2FormViewport"
    @click:outside="closeDialog"
    @close="closeDialog"
    @keydown.esc="closeDialog"
  >
    <v-card>
      <CrudFormDialogHeader
        :kicker="$t('header.droneImageAnalysis')"
        :title="$t('drone.imageErrorAnalysis')"
        show-close
        @close="closeDialog"
      />
      <v-card-text>
        <v-card flat>
          <div class="mt-4 mb-4">
            <v-btn outlined class="solvit-crud-outline-btn solvit-crud-outline-btn--primary" @click="downloadCSV">
              {{ $t('download.errorCoordinates') }}
            </v-btn>
            <span />
          </div>
          <!-- 처리 결과 경고 섹션 -->
          <v-alert
            v-if="imageProcessingResult && hasProcessingWarnings"
            type="warning"
            dense
            class="mb-4"
          >
            <div v-if="imageProcessingResult.failedFiles?.length > 0">
              <strong>{{
                $t('imageLocation.failedFiles', {
                  count: imageProcessingResult.failedFiles.length
                })
              }}</strong>
              <div class="ml-4 mt-1">
                <div v-for="fileName in imageProcessingResult.failedFiles" :key="fileName" class="text-caption">
                  • {{ fileName }}
                </div>
              </div>
            </div>
            <div v-if="imageProcessingResult.filesWithoutGps?.length > 0" class="mt-2">
              <strong>{{
                $t('imageLocation.filesWithoutGps', {
                  count: imageProcessingResult.filesWithoutGps.length
                })
              }}</strong>
              <div class="ml-4 mt-1">
                <div v-for="file in imageProcessingResult.filesWithoutGps" :key="file.fileName" class="text-caption">
                  • {{ file.fileName }}
                </div>
              </div>
            </div>
            <div v-if="imageProcessingResult.filesWithoutExif?.length > 0" class="mt-2">
              <strong>{{
                $t('imageLocation.filesWithoutExif', {
                  count: imageProcessingResult.filesWithoutExif.length
                })
              }}</strong>
              <div class="ml-4 mt-1">
                <div v-for="file in imageProcessingResult.filesWithoutExif" :key="file.fileName" class="text-caption">
                  • {{ file.fileName }}
                </div>
              </div>
            </div>
            <div v-if="imageProcessingResult.filesWithoutDateTime?.length > 0" class="mt-2">
              <strong>{{
                $t('imageLocation.filesWithoutDateTime', {
                  count: imageProcessingResult.filesWithoutDateTime.length
                })
              }}</strong>
              <div class="ml-4 mt-1">
                <div v-for="file in imageProcessingResult.filesWithoutDateTime" :key="file.fileName" class="text-caption">
                  • {{ file.fileName }}
                </div>
              </div>
            </div>
          </v-alert>

          <v-table simple name="image-location-table">
            <v-row>
              <v-col>{{ $t('droneAnalysis.imageCount') }}</v-col>
              <v-col>{{ imagePosition?.length || 0 }} {{ $t('common.count') }}</v-col>
            </v-row>
            <v-row>
              <v-col>{{ $t('droneAnalysis.errorCount') }}</v-col>
              <v-col>{{ recommendImgPos?.length || 0 }} {{ $t('common.count') }}</v-col>
            </v-row>
            <v-row>
              <v-col v-if="Math.round(imageLocationSummary?.median)">
                {{ $t('droneAnalysis.mostFreqDistance') }}
              </v-col>
              <v-col>
                {{
                  Math.round(
                    imageLocationSummary?.median
                      ? imageLocationSummary?.median
                      : null
                  )
                }}
                cm
              </v-col>
            </v-row>
            <v-row>
              <v-col>{{ $t('droneAnalysis.avgDistance') }}</v-col>
              <v-col>
                {{
                  Math.round(
                    imageLocationSummary?.mean
                      ? imageLocationSummary?.mean
                      : null
                  )
                }}
                cm
              </v-col>
            </v-row>
            <v-row>
              <v-col>{{ $t('droneAnalysis.maxDistance') }}</v-col>
              <v-col>
                {{
                  Math.round(
                    imageLocationSummary?.max ? imageLocationSummary?.max : null
                  )
                }}
                cm
              </v-col>
            </v-row>
            <v-row>
              <v-col>{{ $t('droneAnalysis.minDistance') }}</v-col>
              <v-col>{{ Math.round(imageLocationSummary?.min) }} cm</v-col>
            </v-row>
          </v-table>
        </v-card>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import CrudFormDialogHeader from '@/components/common/CrudFormDialogHeader.vue';
import { MODAL_MAX_WIDTH, MODAL_P2_FORM_VIEWPORT } from '@/constants/modalLayout';

export default {
  name: 'ImageLocationDialog',
  components: {
    CrudFormDialogHeader
  },
  props: {
    dialog: {
      type: Boolean,
      required: true
    },
    imageLocationSummary: {
      type: Object,
      required: false
    },
    recommendImgPos: {
      type: Array,
      required: false
    },
    imagePosition: {
      type: Array,
      required: false
    },
    imageProcessingResult: {
      type: Object,
      required: false,
      default: null
    }
  },
  data() {
    return {
      modalLayout: MODAL_MAX_WIDTH,
      modalP2FormViewport: MODAL_P2_FORM_VIEWPORT,
      tabs: null,
      loading: false,
      error: false
    };
  },
  computed: {
    ...mapGetters({
      getUserProfile: 'user/getUserProfile'
    }),
    hasProcessingWarnings() {
      if (!this.imageProcessingResult) return false;
      return (
        (this.imageProcessingResult.failedFiles?.length > 0) ||
        (this.imageProcessingResult.filesWithoutGps?.length > 0) ||
        (this.imageProcessingResult.filesWithoutExif?.length > 0) ||
        (this.imageProcessingResult.filesWithoutDateTime?.length > 0)
      );
    }
  },
  watch: {},
  methods: {
    ...mapActions({}),
    closeDialog() {
      this.handleDialog(false);
    },
    handleDialog(value) {
      this.$emit('close');
    },
    getCurrentDate() {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0'); // 월 보정
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}${month}${day}`;
    },
    downloadCSV() {
      try {
        const headers = ['Number', 'Latitude', 'Longitude'];
        console.log(
          '테스트123',
          this.$props.recommendImgPos,
          this.$props.recommendImgPos.map(
            (point, index) => `${index + 1},${point?.lat},${point?.lon}`
          )
        );
        if (this.$props.recommendImgPos.length === 0) {
          alert(this.$t('imageLocation.noShootingErrors'));
          return;
        }
        const csvContent = [
          headers.join(','), // 헤더 추가
          ...this.$props.recommendImgPos.map(
            (point, index) => `${index + 1},${point?.lat},${point?.lon}`
          )
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute(
          'download',
          `${this.getUserProfile?.userId}_cf_${this.getCurrentDate()}.csv`
        );
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
      } catch (e) {
        alert(this.$t('imageLocation.csvDownloadFailed'));
      }
    },
  }
};
</script>

<style scoped>
.upload-button,
.refresh-button {
  background-color: var(--v-secondary-base);
  color: var(--v-text-base);
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  cursor: pointer;
  border: none;
  margin: 0.25rem;
  transition: background-color var(--solvit-motion-duration) var(--solvit-motion-easing),
    color var(--solvit-motion-duration) var(--solvit-motion-easing);
}

.upload-button:hover,
.refresh-button:hover {
  filter: brightness(0.92);
  color: var(--v-text-base);
}

.error-file {
  border-top: 1px dotted red;
  border-bottom: 1px dotted red;
  margin: 0.25rem 0;
  color: red;
}

.upload-snackbar {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}

.me-2 {
  margin-right: 0.5rem;
}

.text-grey-darken-3 {
  color: var(--v-text-base);
}

.mx-2 {
  margin-left: 0.5rem;
  margin-right: 0.5rem;
}

.custom-file-input .v-input__control .v-input__slot {
  background-color: var(--v-surface-base);
  box-shadow: inset 0px 0px 5px color-mix(in srgb, var(--v-text-base) 30%, transparent); /* 안쪽 그림자 */
  border-radius: 4px; /* 테두리 모양 변경 */
  transition: opacity var(--solvit-motion-duration) var(--solvit-motion-easing),
    transform var(--solvit-motion-duration) var(--solvit-motion-easing);
  cursor: pointer !important; /* 커서 모양 변경 */
}
</style>
